import React from "react";
import moment from "moment";
import {
    Badge, Box, Button, Collapse, Flex, IconButton, Menu, MenuButton, MenuDivider, MenuItem, MenuList,
    Select, Spinner, Text, useDisclosure
} from "@chakra-ui/react";
import {FaEllipsisH, FaFileExcel, FaFilter, FaSortAmountDown, FaSortAmountUp} from "react-icons/fa";
import {EmployeeData, PIMetadata} from "../../api/performanceAPI";
import {
    BookingDecision, BookingStatus, BookingStatusMap, CustomerOrStrypes, PIFilters
} from "./HeaderFiltersComponent";
import handleExport from "./tableGenerator";
import {DebouncedInput} from "./DebouncedInput";

interface MobilePIViewProps {
    months: string[];
    currentMonth: string;
    setCurrentMonth: (m: string) => void;
    filters: PIFilters;
    onFilterChange: (field: keyof PIFilters, value: string) => void;
    rows: EmployeeData[];
    filteredData: EmployeeData[];
    decisions: BookingStatusMap;
    onDecisionChange: (id: string, d: BookingDecision) => void;
    bulkUpdate: (status: BookingStatus, clearAll?: boolean, report?: boolean) => void;
    bulkMark: (requested: CustomerOrStrypes) => void;
    monthCompleted: boolean;
    metaData: PIMetadata | undefined;
    sortDirection: "asc" | "desc" | null;
    toggleDateSort: () => void;
    loading: boolean;
}

/** Menu items are <button>s, so legacy AUI/Bootstrap button borders and shadows have to be reset explicitly. */
const menuItemStyle = {
    border: "none",
    boxShadow: "none",
    backgroundImage: "none",
    borderRadius: "var(--chakra-radii-lg)",
    minHeight: "44px",
    padding: "0 12px",
    fontSize: "15px",
    fontWeight: 500,
    textAlign: "left",
    background: "transparent",
    _hover: {background: "var(--chakra-colors-surface-subtle)"},
    _focus: {background: "var(--chakra-colors-surface-subtle)"},
};

function effectiveStatus(row: EmployeeData, d?: BookingDecision): BookingStatus | undefined {
    if (d?.status !== undefined) return d.status;
    if (row.approved) return "approve";
    if (row.approved !== null && row.approved !== undefined && Number(row.approved) === 0) return "reject";
    return undefined;
}

function effectiveRequested(row: EmployeeData, d?: BookingDecision): string | undefined {
    return d?.requested ?? (row.customerOrStrypes || undefined);
}

/** Segmented two-way control sized for thumbs. */
function Segment(props: {
    options: {value: string; label: string; scheme: string}[];
    value: string | undefined;
    onPick: (v: string) => void;
    label: string;
}) {
    return (
        <Flex role="group" aria-label={props.label} bg="surface-subtle" borderRadius="lg" p="3px" gap="3px">
            {props.options.map((o) => {
                const active = props.value === o.value;
                return (
                    <Button key={o.value} flex="1" h="40px" borderRadius="md" fontSize="sm" fontWeight={600}
                            variant="unstyled" display="inline-flex" alignItems="center" justifyContent="center"
                            bg={active ? `${o.scheme}.500` : "transparent"}
                            color={active ? "white" : "text-muted"}
                            boxShadow={active ? "sm" : "none"}
                            _hover={{bg: active ? `${o.scheme}.600` : "blackAlpha.50"}}
                            _dark={{_hover: {bg: active ? `${o.scheme}.600` : "whiteAlpha.100"}}}
                            transition="background-color .15s ease, color .15s ease"
                            aria-pressed={active} onClick={() => props.onPick(o.value)}>
                        {o.label}
                    </Button>
                );
            })}
        </Flex>
    );
}

function PICardImpl(props: {row: EmployeeData; decision?: BookingDecision; onChange: (id: string, d: BookingDecision) => void}) {
    const {row, decision, onChange} = props;
    const status = effectiveStatus(row, decision);
    const requested = effectiveRequested(row, decision);
    return (
        <Box borderWidth="1px" borderColor="border-subtle" borderRadius="xl" p={3} mb={3} bg="surface"
             borderLeftWidth="4px"
             borderLeftColor={status === "approve" ? "green.400" : status === "reject" ? "red.400" : "border-subtle"}>
            <Flex justify="space-between" align="flex-start" gap={3}>
                <Box minW={0}>
                    <Text fontWeight={700} noOfLines={1}>{row.FirstName}</Text>
                    <Text fontSize="xs" color="text-muted">
                        {moment(row.WorkDate).format("ddd, DD MMM YYYY")} · {row.DepartmentName}
                    </Text>
                </Box>
                <Box textAlign="right" flexShrink={0}>
                    <Text fontSize="xl" fontWeight={700} lineHeight="1.1">{row.AddedValue}<Text as="span" fontSize="xs" color="text-muted">h</Text></Text>
                    <Text fontSize="xs" fontWeight={600} color="text-muted">{Math.round(row.IncentivePercentage * 100)}% PI</Text>
                </Box>
            </Flex>

            <Box mt={2} fontSize="sm">
                <Text fontWeight={600}>{row.Project}</Text>
                {row.Note && <Text color="text-muted" noOfLines={3}>{row.Note}</Text>}
            </Box>

            <Flex mt={2} gap={2} flexWrap="wrap" fontSize="xs" color="text-muted" align="center">
                <Text>Manager: {row.GM}</Text>
                <Badge colorScheme={row.IsInternalEmployee ? "teal" : "purple"}>{row.IsInternalEmployee ? "Internal" : "External"}</Badge>
                <Text>#{row.BookingID}</Text>
            </Flex>
            {row.approvalLog && <Text mt={1} fontSize="xs" color="text-muted">{row.approvalLog}</Text>}

            <Text mt={3} mb={1} fontSize="10px" fontWeight={700} textTransform="uppercase" letterSpacing="0.06em" color="text-muted">Requested by</Text>
            <Segment label="Requested by" value={requested}
                     options={[
                         {value: "Customer Requested", label: "Customer", scheme: "orange"},
                         {value: "Strypes Requested", label: "Strypes", scheme: "green"},
                     ]}
                     onPick={v => onChange(row.BookingID, {requested: v as CustomerOrStrypes, status: decision?.status})}/>

            <Text mt={3} mb={1} fontSize="10px" fontWeight={700} textTransform="uppercase" letterSpacing="0.06em" color="text-muted">Decision</Text>
            <Segment label="Decision" value={status}
                     options={[
                         {value: "approve", label: "Approve", scheme: "green"},
                         {value: "reject", label: "Reject", scheme: "red"},
                     ]}
                     onPick={v => onChange(row.BookingID, {requested: decision?.requested, status: v as BookingStatus})}/>
        </Box>
    );
}

const PICard = React.memo(PICardImpl, (a, b) =>
    a.row === b.row && a.decision?.status === b.decision?.status && a.decision?.requested === b.decision?.requested);

export function MobilePIView(p: MobilePIViewProps) {
    const filtersDisclosure = useDisclosure();
    const activeFilters = [p.filters.name, p.filters.manager, p.filters.project].filter(Boolean).length;
    const pending = Object.keys(p.decisions).length;

    return (
        <Box px={3} pt={2} pb="96px" width="100%" minH="calc(100dvh - 72px)" bg="surface">
            <Text as="h1" fontSize="xl" fontWeight={700} textAlign="center" mt={4} mb={4}>Performance Incentive</Text>

            {p.loading && (
                <Flex position="fixed" inset={0} bg="rgba(255,255,255,0.7)" _dark={{bg: "rgba(20,20,20,0.7)"}} align="center" justify="center" zIndex={9999}>
                    <Spinner size="xl" color="blue.500"/>
                </Flex>
            )}

            <Flex gap={2} mb={2}>
                <Select aria-label="Month" value={p.currentMonth} onChange={e => p.setCurrentMonth(e.target.value)} flex="1">
                    {p.months.map(m => <option key={m} value={m}>{m}</option>)}
                </Select>
                <IconButton aria-label="Sort by date" onClick={p.toggleDateSort} variant="outline"
                            icon={p.sortDirection === "asc" ? <FaSortAmountUp/> : <FaSortAmountDown/>}
                            colorScheme={p.sortDirection ? "blue" : "gray"}/>
                <Button leftIcon={<FaFilter/>} variant="outline" onClick={filtersDisclosure.onToggle}
                        colorScheme={activeFilters ? "blue" : "gray"}>
                    {activeFilters ? `Filters (${activeFilters})` : "Filters"}
                </Button>
            </Flex>

            <Collapse in={filtersDisclosure.isOpen} animateOpacity>
                <Flex direction="column" gap={2} pb={2}>
                    <DebouncedInput placeholder="Filter by name" value={p.filters.name} onCommit={v => p.onFilterChange("name", v)}/>
                    <DebouncedInput placeholder="Filter by manager" value={p.filters.manager} onCommit={v => p.onFilterChange("manager", v)}/>
                    <DebouncedInput placeholder="Filter by project ID" value={p.filters.project} onCommit={v => p.onFilterChange("project", v)}/>
                </Flex>
            </Collapse>

            <Flex justify="space-between" align="center" mb={2} fontSize="xs" color="text-muted">
                <Text>{p.rows.length} {p.rows.length === 1 ? "entry" : "entries"}</Text>
                <Text>{pending > 0 ? `${pending} unsaved decision${pending === 1 ? "" : "s"}` : p.monthCompleted ? "Month completed" : ""}</Text>
            </Flex>

            {p.rows.length === 0 && !p.loading && (
                <Box borderWidth="1px" borderStyle="dashed" borderColor="border-subtle" borderRadius="xl" py={10} textAlign="center">
                    <Text color="text-muted">No entries for this month</Text>
                </Box>
            )}

            {p.rows.map(row => (
                <PICard key={row.BookingID} row={row} decision={p.decisions[row.BookingID]}
                        onChange={p.onDecisionChange}/>
            ))}

            {/* Thumb-reach action bar */}
            <Flex position="fixed" left={0} right={0} bottom={0} zIndex={20} gap={2} p={3}
                  bg="surface" borderTopWidth="1px" borderColor="border-subtle"
                  boxShadow="0 -4px 12px rgba(0,0,0,0.08)" pb="calc(12px + env(safe-area-inset-bottom))">
                {!p.monthCompleted && <>
                    <Button flex="1" colorScheme="green" onClick={() => p.bulkUpdate("approve")}>Approve all</Button>
                    <Button flex="1" colorScheme="red" variant="outline" onClick={() => p.bulkUpdate("reject")}>Reject all</Button>
                </>}
                <Menu placement="top-end">
                    <MenuButton as={IconButton} aria-label="More actions" icon={<FaEllipsisH/>} variant="outline"
                                flex={p.monthCompleted ? "1" : undefined}/>
                    <MenuList bg="surface" borderWidth="1px" borderColor="border-subtle" borderRadius="xl" boxShadow="xl" p={2} minW="240px">
                        {!p.monthCompleted && <>
                            <MenuItem sx={menuItemStyle} onClick={() => p.bulkMark("Customer Requested")}>Set all Customer Requested</MenuItem>
                            <MenuItem sx={menuItemStyle} onClick={() => p.bulkMark("Strypes Requested")}>Set all Strypes Requested</MenuItem>
                            <MenuItem sx={menuItemStyle} onClick={() => p.bulkUpdate("approve", true)}>Clear decisions</MenuItem>
                            <MenuDivider my={1}/>
                        </>}
                        <MenuItem sx={menuItemStyle} icon={<FaFileExcel/>} onClick={() => handleExport(p.filteredData, p.metaData as PIMetadata)}>Export to Excel</MenuItem>
                        {!p.monthCompleted && (
                            <MenuItem sx={{...menuItemStyle, mt: 1, fontWeight: 700, color: "white", background: "var(--chakra-colors-green-500)", _hover: {background: "var(--chakra-colors-green-600)"}, _focus: {background: "var(--chakra-colors-green-600)"}}}
                                      onClick={() => p.bulkUpdate("approve", true, true)}>Finalize review</MenuItem>
                        )}
                    </MenuList>
                </Menu>
            </Flex>
        </Box>
    );
}
